import React, { useRef, useEffect, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import hexToRgba from 'hex-to-rgba'

import Draggable from '@client/components/DraggableWrapper'
import {
  BlockModel,
  NULL as BlockModelNULL,
} from '@client/components/Block/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import { Table } from '@client/components/Tables'
import { FlexContainer } from '@client/components/FlexContainer'
import CustomEvent from '@client/components/CustomEvent/index'
import ColumnModel, {
  NULL as ColumnModelNULL,
} from '@client/components/DroppableWrapper/model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import {
  ITEM_TYPE_FORM,
  ITEM_TYPE_MAIL,
  ITEM_TYPE_CONFIRMATION_MAIL,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_RESPONDER,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
  ITEM_TYPE_SURVEY,
  ITEM_TYPE_SURVEY_THANK_YOU,
  WIZARD_TYPE_PAGE,
} from '@client/components/Editor/settings'
import { cancelStorage } from '@client/utils/Storage'
import { BLOCK_TYPES } from '@client/components/Block/constants'
import Toolbar from '@client/components/Toolbar/index'
import { SECTION_LAYOUTS } from './constants'

import './styles.scss'

const BottomSpacing = ({ kind, height = '0' }) => {
  const heightWithPx = height + 'px'

  if (
    kind !== ITEM_TYPE_MAIL &&
    kind !== ITEM_TYPE_RESPONDER &&
    kind !== ITEM_TYPE_CONFIRMATION_MAIL
  ) {
    return (
      <div style={{ width: '100%', height: heightWithPx, }} />
    )
  }

  const styles = {
    width: '100%',
    border: 'none',
    height: heightWithPx,
    lineHeight: heightWithPx,
    maxHeight: heightWithPx,
    fontSize: heightWithPx,
  }

  return (
    <table
      className="bottom-spacing"
      role="presentation"
      cellSpacing="0"
      cellPadding="0"
      width="100%"
      border="0"
    >
      <tbody>
        <tr>
          <td
            width="100%"
            height={heightWithPx}
            style={styles}
            dangerouslySetInnerHTML={{
              __html: '&nbsp;',
            }}
          />
        </tr>
      </tbody>
    </table>
  )
}

BottomSpacing.propTypes = {
  height: PropTypes.string,
}

const Section = React.forwardRef((props, ref) => {
  const {
    app = AppModelNULL,
    model = BlockModelNULL,
    editor = EditorModelNULL,
    index = 0,
    container = ColumnModelNULL,
    serverRender = false,
    count = 0,
  } = props

  const sectionRef = useRef(null)

  // Expose DOM element via useImperativeHandle
  useImperativeHandle(ref, () => ({
    getDOMElement: () => sectionRef.current,
    // Expose any other methods you might need
    getModel: () => model,
    getEditor: () => editor,
  }), [model, editor])

  // Set up model properties
  useEffect(() => {
    if (model) {
      model.index = index
      model.container = container
    }
  }, [model, index, container])

  const handleMouseOver = (event) => {
    if (model && model.setShowToolbar) {
      model.setShowToolbar(event.track[0] === event.listenerElement)
    }
  }

  const handleMouseLeave = (event) => {
    if (model && model.setShowToolbar) {
      model.setShowToolbar(event.track[0] !== event.listenerElement)
    }
  }

  const getBorderRadius = () => {
    const borderRadius = editor.settings.values.popup_borderRadius
      ? `${editor.settings.values.popup_borderRadius}px`
      : undefined

    if (index === 0 && count === 1) {
      return borderRadius
    }

    if (index === 0 && count > 1) {
      return `${borderRadius} ${borderRadius} 0 0`
    }

    if (index === count - 1 && count > 1) {
      return `0 0 ${borderRadius} ${borderRadius}`
    }

    return undefined
  }

  const handleEdit = (event) => {
    if (!model.showToolbar || serverRender) {
      return
    }
    if ((editor.kind === ITEM_TYPE_SURVEY || editor.kind === ITEM_TYPE_FORM) && model.type === BLOCK_TYPES.SECTION) {
      return
    }
    const item = editor.serialize()
    cancelStorage.saveItem(item)
    app.setEditingSettings(model)
  }

  if (app.wizard.type === WIZARD_TYPE_PAGE && (
    editor.kind === ITEM_TYPE_PAGE ||
    editor.kind === ITEM_TYPE_LANDING_PAGE ||
    editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS)
  ) {
    let className = ''
    let styles = {}
    if (model.settings.values.layout === SECTION_LAYOUTS[0].value) {
      className = `content-background relative padding`
      styles = {
        ...styles,
        ...model.borderStyles,
        ...model.borderRadiusStyles,
        paddingLeft: typeof model.settings.values.sidePadding !== 'undefined'
          ? `${model.settings.values.sidePadding}px`
          : undefined,
        paddingRight: typeof model.settings.values.sidePadding !== 'undefined'
          ? `${model.settings.values.sidePadding}px`
          : undefined,
      }
    } else if (model.settings.values.layout === SECTION_LAYOUTS[1].value) {
      className = `content-width-margin relative`
    } else if (model.settings.values.layout === SECTION_LAYOUTS[2].value) {
      className = `content-width-margin relative`
    }

    const backgroundColor = model.isFullWidth
      ? model.settings.values.backgroundColor
        ? model.settings.values.backgroundColor
        : editor.settings.values.contentColor
      : ''
    let backgroundImage =
      model.isFullWidth && model.settings.values.hasBackgroundImage
        ? model.settings.values.backgroundImageUrl || ''
        : ''

    const tableCellStyles = {}

    tableCellStyles.backgroundPosition = 'center'
    tableCellStyles.backgroundSize = 'cover'

    const style = {
      ...tableCellStyles,
      ...styles,
      backgroundColor: backgroundColor,
      backgroundImage: backgroundImage
        ? `url('${backgroundImage}')`
        : undefined,
      boxShadow:
        backgroundImage &&
        model.settings.values.hasBackgroundImageOverlay &&
        model.settings.values.backgroundColorOverlay
          ? `inset 0 0 0 100vmax ${hexToRgba(model.settings.values.backgroundColorOverlay)}`
          : undefined
    }

    return (
      <Draggable data={model} move={true}>
        <CustomEvent
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className={serverRender ? '' : 'section-container'}
            style={{
              width: '100%',
              borderRadius: getBorderRadius(),
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
            onClick={handleEdit}
            ref={sectionRef}
          >
            <section
              className={`${className} ${model.showToolbar ? 'show-toolbar' : ''}`}
              style={style}
            >
              <Toolbar model={model} count={count} />
              <FlexContainer
                className={`section `}
                model={model}
                index={index}
                count={count}
              />
            </section>
            <If condition={model.settings.values.hasBottomSpacing}>
              <BottomSpacing height={model.settings.values.bottomSpacing} kind={editor.kind} />
            </If>
          </div>
        </CustomEvent>
      </Draggable>
    )
  }

  return (
    <Draggable data={model} move={true}>
      <CustomEvent
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={serverRender ? '' : 'section-container'}
          style={{
            width:
              editor.kind === ITEM_TYPE_FORM ||
              editor.kind === ITEM_TYPE_MAIL ||
              editor.kind === ITEM_TYPE_CONFIRMATION_MAIL ||
              editor.kind === ITEM_TYPE_RESPONDER
                ? undefined
                : '100%',
            borderRadius: getBorderRadius(),
            ...(editor.kind === ITEM_TYPE_MAIL ||
            editor.kind === ITEM_TYPE_CONFIRMATION_MAIL ||
            editor.kind === ITEM_TYPE_RESPONDER
              ? {
                  borderRadius: undefined,
                }
              : {
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }),
          }}
          onClick={handleEdit}
          ref={sectionRef}
        >
          {editor.kind === ITEM_TYPE_FORM ||
          editor.kind === ITEM_TYPE_PAGE ||
          editor.kind === ITEM_TYPE_LANDING_PAGE ||
          editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS ||
          editor.kind === ITEM_TYPE_SURVEY ||
          editor.kind === ITEM_TYPE_SURVEY_THANK_YOU ? (
            <FlexContainer
              className={`section ${model.showToolbar ? 'show-toolbar' : ''}`}
              model={model}
              width={
                editor.viewMode !== 'mobile' && editor.popupWidth
                  ? `${editor.popupWidth}px`
                  : undefined
              }
              index={index}
              count={count}
            />
          ) : (
            <Table
              className={`section ${model.showToolbar ? 'show-toolbar' : ''}`}
              model={model}
              count={count}
            />
          )}
          <If condition={model.settings.values.hasBottomSpacing}>
            <BottomSpacing height={model.settings.values.bottomSpacing} kind={editor.kind} />
          </If>
        </div>
      </CustomEvent>
    </Draggable>
  )
})

Section.propTypes = {
  app: PropTypes.instanceOf(AppModel),
  model: PropTypes.instanceOf(BlockModel),
  editor: PropTypes.instanceOf(EditorModel),
  index: PropTypes.number,
  container: PropTypes.instanceOf(ColumnModel),
  serverRender: PropTypes.bool,
  count: PropTypes.number,
}

Section.defaultProps = {
  app: AppModelNULL,
  model: BlockModelNULL,
  editor: EditorModelNULL,
  index: 0,
  container: ColumnModelNULL,
  serverRender: false,
  count: 0,
}

// Apply MobX injection and observer
const ObservedSection = inject('editor', 'serverRender', 'app')(observer(Section))

export default ObservedSection
